/** Состояние страницы «WB: регионы» в адресе: ссылкой можно поделиться и снять скриншот.
 *
 *  Параметры (умолчание — параметра нет):
 *    legal   кабинет (имя из /meta, только активные) или `all` — оба; нет — первый активный из
 *            /meta (страница дописывает его в адрес). Неизвестный — ошибка с кнопкой сброса.
 *    tab     вкладка: deficit (умолчание) | supplies | checks | draft; неизвестная — ошибка с кнопкой сброса
 *    q       поиск оффера (на бэкенде: артикул, nm, артикул/название 1С, название WB; во вкладке
 *            «Подбор поставки» — те же поля, на фронте)
 *    def=1   только офферы с дефицитом (есть ячейка `deficit_class` zero/low); в «Подборе
 *            поставки» — товары, у которых такой класс есть во включённом регионе
 *    nd=1    скрыть офферы без спроса (итог оффера `no_demand`)
 *    focus   регион-фокус матрицы: имя макрорегиона | abroad | unknown (клик по строке региона)
 *    abroad=1  группа «Зарубеж» в таблице регионов раскрыта
 *    card    карточка оффера `offer:<кабинет>:<offer_key>` или поставки `supply:<кабинет>:<supply_id>`
 *            (кабинет — свой у объекта и при `all`)
 *  Вкладка «Поставки» (нет параметра — умолчание из /meta.supply_defaults; `-` — фильтр снят):
 *    st      статусы хранения и/или `planned` (запланирована) через запятую | `-` — все статусы
 *    since   supply_date ≥ (YYYY-MM-DD) | `-` — без даты
 *    flags=1 только с флагами
 *    method  метод оценки склада поставки
 *    sq      поиск по номеру поставки
 *  Вкладка «Подбор поставки» (черновик отметок и количеств и «Куда планируем» — в localStorage,
 *  не в адресе; регионы выбираются галочками там же, прежние `reg` и `rx` не читаются):
 *    marked=1  только отмеченные товары
 *    off=1   показывать выключенные регионы (серым; иначе скрыты со счётчиком у товара)
 */
import { WB_ALL, type SupplyDefaults, type SupplyQuery, type SupplyStatusFilter } from "@/api/wbRegions";

/** Вкладки по порядку: строка здесь + ветка на странице. */
export const TABS = [
  { id: "deficit", label: "Дефицит" },
  { id: "supplies", label: "Поставки" },
  { id: "checks", label: "Проверки" },
  { id: "draft", label: "Подбор поставки" },
] as const;
export type Tab = (typeof TABS)[number]["id"];
const DEFAULT_TAB: Tab = "deficit";

/** Столбец матрицы / ключ фокуса: макрорегион по имени, страны — `abroad`, не определён —
 *  `unknown`. */
export const ABROAD = "abroad";
export const UNKNOWN = "unknown";

export interface OfferCardRef {
  kind: "offer";
  legal: string;
  offerKey: string;
}

export interface SupplyCardRef {
  kind: "supply";
  legal: string;
  supplyId: number;
}

export type CardRef = OfferCardRef | SupplyCardRef;

export type CardParam = { card: CardRef | null; error: null } | { card: null; error: string };

const OFFER_KEY_RE = /^[0-9a-f]{32}$/;
export const SUPPLY_ID_RE = /^[1-9][0-9]{0,17}$/;

/** `card=offer:<кабинет>:<offer_key>` | `card=supply:<кабинет>:<supply_id>`. Ключ — после
 *  ПОСЛЕДНЕГО `:` (в имени кабинета `:` допустим). Неверная ссылка — не молча «без карточки», а
 *  ошибка на экране. */
export function parseCard(v: string | null): CardParam {
  if (!v) return { card: null, error: null };
  const bad = { card: null, error: `ссылка на карточку «${v}» не распознана` } as const;
  const kind = v.startsWith("offer:") ? "offer" : v.startsWith("supply:") ? "supply" : null;
  if (!kind) return bad;
  const rest = v.slice(kind.length + 1);
  const i = rest.lastIndexOf(":");
  if (i <= 0) return bad;
  const legal = rest.slice(0, i);
  const id = rest.slice(i + 1);
  if (kind === "offer") {
    if (!OFFER_KEY_RE.test(id)) return bad;
    return { card: { kind, legal, offerKey: id }, error: null };
  }
  if (!SUPPLY_ID_RE.test(id)) return bad;
  return { card: { kind, legal, supplyId: Number(id) }, error: null };
}

export function cardValue(c: CardRef): string {
  return `${c.kind}:${c.legal}:${c.kind === "offer" ? c.offerKey : String(c.supplyId)}`;
}

export type TabParam = { tab: Tab; unknown: null } | { tab: null; unknown: string };

export function parseTab(params: URLSearchParams): TabParam {
  const v = params.get("tab");
  if (!v) return { tab: DEFAULT_TAB, unknown: null };
  const t = TABS.find((x) => x.id === v);
  return t ? { tab: t.id, unknown: null } : { tab: null, unknown: v };
}

export interface PageState {
  /** как в адресе (null — не задан); проверка по /meta — `legalStatus` */
  legal: string | null;
  tab: TabParam;
  q: string;
  deficitOnly: boolean;
  hideNoDemand: boolean;
  focus: string | null;
  abroadOpen: boolean;
  card: CardParam;
  supply: SupplyParams;
  draft: DraftParams;
}

/** Фильтры «Подбора поставки» (поиск и «только дефицит» — общие `q`, `def`). */
export interface DraftParams {
  markedOnly: boolean;
  /** показывать выключенные регионы («Куда планируем») */
  showOff: boolean;
}

/** Фильтры «Поставок» как в адресе: undefined — параметра нет (берётся умолчание /meta), null —
 *  фильтр снят (`-`). */
export interface SupplyParams {
  st: string[] | null | undefined;
  since: string | null | undefined;
  flagsOnly: boolean;
  method: string | null;
  sq: string;
}

/** Снятый фильтр в адресе (иначе «нет параметра» = умолчание). */
export const OFF = "-";

function readSupply(params: URLSearchParams): SupplyParams {
  const st = params.get("st");
  const since = params.get("since");
  return {
    st: st === null ? undefined : st === OFF ? null : st.split(",").filter(Boolean),
    since: since === null ? undefined : since === OFF ? null : since,
    flagsOnly: params.get("flags") === "1",
    method: params.get("method") || null,
    sq: params.get("sq") ?? "",
  };
}

export type SupplyQueryResult =
  | { query: SupplyQuery; error: null }
  | { query: null; error: string };

/** Фильтры адреса + умолчания /meta → запрос `/supplies`. Статус вне /meta.status_filters —
 *  ошибка (не молча «без фильтра»). */
export function supplyQuery(
  p: SupplyParams,
  defaults: SupplyDefaults,
  statuses: readonly string[],
): SupplyQueryResult {
  const st = p.st === undefined ? defaults.status : p.st;
  const bad = (st ?? []).filter((x) => !statuses.includes(x));
  if (bad.length) {
    return { query: null, error: `Статус хранения «${bad.join(", ")}» в адресе не из витрины (есть: ${statuses.join(", ")}).` };
  }
  return {
    query: {
      status: st && st.length ? (st as SupplyStatusFilter[]) : null,
      since: p.since === undefined ? defaults.since : p.since,
      flagsOnly: p.flagsOnly,
      method: p.method,
      q: p.sq.trim() || null,
    },
    error: null,
  };
}

/** Сброс фильтров «Поставок» к умолчаниям /meta. */
export const RESET_SUPPLY_FILTERS: Patch = { st: null, since: null, flags: null, method: null, sq: null };

export function readState(params: URLSearchParams): PageState {
  return {
    legal: params.get("legal") || null,
    tab: parseTab(params),
    q: params.get("q") ?? "",
    deficitOnly: params.get("def") === "1",
    hideNoDemand: params.get("nd") === "1",
    focus: params.get("focus") || null,
    abroadOpen: params.get("abroad") === "1",
    card: parseCard(params.get("card")),
    supply: readSupply(params),
    draft: {
      markedOnly: params.get("marked") === "1",
      showOff: params.get("off") === "1",
    },
  };
}

/** Кабинет адреса против /meta: `missing` — не задан (страница подставит первый активный),
 *  `unknown` — нет среди активных и не `all` (ошибка, запросов нет). */
export type LegalStatus =
  | { kind: "ok"; legal: string }
  | { kind: "missing"; first: string | null }
  | { kind: "unknown"; legal: string };

export function legalStatus(legal: string | null, active: string[]): LegalStatus {
  if (legal === null) return { kind: "missing", first: active[0] ?? null };
  if (legal === WB_ALL || active.includes(legal)) return { kind: "ok", legal };
  return { kind: "unknown", legal };
}

export type Patch = Record<string, string | null>;

export function applyPatch(prev: URLSearchParams, patch: Patch): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const [k, v] of Object.entries(patch)) {
    if (v === null || v === "") next.delete(k);
    else next.set(k, v);
  }
  return next;
}

/** Сброс фильтров матрицы (поиск, «только с дефицитом», «без спроса», фокус). */
export const RESET_FILTERS: Patch = { q: null, def: null, nd: null, focus: null };

/** Сброс фильтров товаров «Подбора поставки» (`off` товары не прячет — не сбрасывается). */
export const RESET_DRAFT_FILTERS: Patch = { q: null, def: null, marked: null };
